import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Eye, Award, Users, Wrench, Package } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';



import infra2 from '../assets/Images/infra/img2.jpg';
import infra3 from '../assets/Images/infra/img3.jpg';
import infra4 from '../assets/Images/infra/img4.jpg';
import infra5 from '../assets/Images/infra/img5.jpg';
import infra6 from '../assets/Images/infra/img6.jpg';
import profile from '../assets/Images/profile.jpg';
import about from '../assets/Images/about.jpeg';

const About = () => {
  const navigate = useNavigate();
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: <Award size={32} />,
      title: 'Quality Excellence',
      description: 'We never compromise on quality, using only genuine parts and proven repair methods.'
    },
    {
      icon: <Users size={32} />,
      title: 'Customer First',
      description: 'Your satisfaction and equipment uptime are our top priorities in every service we provide.'
    },
    {
      icon: <Wrench size={32} />,
      title: 'Technical Expertise',
      description: 'Continuous training ensures our team stays ahead with the latest hydraulic technologies.'
    },
    {
      icon: <Package size={32} />,
      title: 'Reliability',
      description: 'We deliver on our promises with consistent, dependable service you can count on.'
    }
  ];

  return (
    <div className="App">
      <Header />

      {/* Page Header */}
      <section className="page-header">
        <div className="page-header-content">
          <h1 className="fade-up">About Us</h1>
          <p className="fade-up delay-200">Leading the way in heavy equipment hydraulic solutions</p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="content-section">
        <div className="content-container">
          <div className="content-grid">
            <div className="content-text" ref={(el) => (sectionsRef.current[0] = el)}>
              <h2>Our Story</h2>
              <p>
                Established in 2022, what began as a small workshop with a vision has grown Hospet Hydraulic Solutions into a trusted name in hydraulic systems and refurbishment. Our journey started with a simple mission: to provide reliable, high-quality service that keeps industries moving forward.
              </p>
              <p>
                At Hospet Hydraulic Solutions, we've built our reputation on a foundation of technical excellence, integrity, and an unwavering commitment to our clients. Every project, no matter the size, is approached with the same dedication to quality and precision that has defined us from the beginning.
              </p>

              <div className="mt-8 space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-[#00325b] mb-3">Specialization</h3>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Wrench className="h-5 w-5 text-blue-600" />
                    </div>
                    <p className="ml-3 text-gray-600">
                      Maintenance and refurbishment of hydraulic systems.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-[#00325b] mb-3">Industries Served</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {['Mining', 'Construction', 'Quarrying', 'Industrial Equipment'].map((industry) => (
                      <div key={industry} className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-2 w-2 rounded-full bg-blue-600"></div>
                        </div>
                        <span className="ml-3 text-gray-600">{industry}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="content-image">
              <img
                src={about}
                alt="Professional Team"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-vision-section">
        <div className="content-container">
          <div className="mission-vision-grid">
            <div className="mission-vision-item mb-12" ref={(el) => (sectionsRef.current[1] = el)}>
              <div className="flex items-center gap-4 mb-4">
                <div className="text-blue-600">
                  <Target size={40} />
                </div>
                <h3 className="text-2xl font-semibold text-[#00325b]">Our Mission</h3>
              </div>
              <p className="text-gray-600 pl-14">
                To provide dependable and precision-driven hydraulic maintenance solutions that maximize machine uptime, enhance operational efficiency, and ensure long-term reliability of heavy equipment through advanced technology, skilled expertise, and a commitment to quality service.
              </p>
            </div>
            <div className="mission-vision-item" ref={(el) => (sectionsRef.current[2] = el)}>
              <div className="flex items-center gap-4 mb-4">
                <div className="text-blue-600">
                  <Eye size={40} />
                </div>
                <h3 className="text-2xl font-semibold text-[#00325b]">Our Vision</h3>
              </div>
              <p className="text-gray-600 pl-14">
                To be the most trusted and recognized name in hydraulic refurbishment across South India by delivering exceptional quality, innovative solutions, and customer-focused services that set new benchmarks in reliability and performance.              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure & Core Values */}
      <section className="py-16 bg-white">
        <div className="content-container">
          <div className="grid md:grid-cols-2 gap-12 items-center">

            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img
                  src={infra2}
                  alt="Workshop Equipment"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img
                  src={infra3}
                  alt="Hydraulic Systems"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img
                  src={infra4}
                  alt="Technical Team"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img
                  src={infra5}
                  alt="Quality Control"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>


            <div>
              <h2 className="text-3xl font-bold text-[#00325b] mb-8">Our Infrastructure</h2>

              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold text-[#00325b] mb-4">Facility</h3>
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <p className="text-gray-700 mb-4">
                      <span className="font-medium">5000 sq.ft.</span> dedicated workshop space equipped with:
                    </p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {[
                        'Lathe Machines', 'Drilling Machines', 'Welding Equipment',
                        'Pressing Machines', 'Air Compressors', 'Precision Measuring Tools', 'Surface Grinding Machines', 'KeyWay Sloting Machines', 'Power Hacksaw machines',
                      ].map((item, index) => (
                        <li key={index} className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-blue-600 mr-3"></div>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-[#00325b] mb-4">Core Values</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { icon: <Award size={24} className="text-blue-600" />, title: 'Quality' },
                      { icon: <Package size={24} className="text-blue-600" />, title: 'Reliability' },
                      { icon: <Wrench size={24} className="text-blue-600" />, title: 'Technical Expertise' },
                      { icon: <Users size={24} className="text-blue-600" />, title: 'Client Focus' }
                    ].map((value, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg flex items-center space-x-3">
                        <div className="bg-blue-100 p-2 rounded-full">
                          {value.icon}
                        </div>
                        <span className="font-medium text-gray-800">{value.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="content-section" style={{ background: '#f8f9fa' }}>
        <div className="content-container">
          <h2 className="section-title">Our Expertise</h2>
          <div className="expertise-grid">
            <div className="expertise-item" ref={(el) => (sectionsRef.current[3] = el)}>
              <div className="expertise-number">1.</div>
              <div className="expertise-content">
                <h3>Hydraulic System Troubleshooting</h3>
                <p>
                  Advanced diagnostic services to quickly identify and resolve hydraulic issues with precision.
                </p>
              </div>
            </div>
            <div className="expertise-item" ref={(el) => (sectionsRef.current[4] = el)}>
              <div className="expertise-number">2.</div>
              <div className="expertise-content">
                <h3>General Machining Works</h3>
                <p>
                  Precision machining and fabrication of custom components for repair and maintenance needs.
                </p>
              </div>
            </div>
            <div className="expertise-item" ref={(el) => (sectionsRef.current[5] = el)}>
              <div className="expertise-number">3.</div>
              <div className="expertise-content">
                <h3>Aftermarket Sales</h3>
                <p>
                  Supplying genuine hydraulic parts including pumps, motors, seal kits, and other key components.
                </p>
              </div>
            </div>
            <div className="expertise-item" ref={(el) => (sectionsRef.current[6] = el)}>
              <div className="expertise-number">4.</div>
              <div className="expertise-content">
                <h3>Expert Team</h3>
                <p>
                  Skilled technicians and engineers trained in the latest hydraulic systems and technologies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      <section className="team-section">
        <div className="content-container">
          <h2 className="section-title" ref={(el) => (sectionsRef.current[11] = el)}>Our Expert Team</h2>
          <p className="section-subtitle">Meet the professionals who make it all possible</p>

          <div className="team-grid">
            <div className="team-member" ref={(el) => (sectionsRef.current[12] = el)}>
              <div className="team-member-image">
                <img
                  src={profile}
                  alt="Shivakumar - Hydraulic Systems Expert"
                />
              </div>
              <h3>Shivakumar</h3>
              <p className="role">Founder & CEO - Hydraulic Systems Expert</p>
              <p className="bio">With 20 years of specialized experience in hydraulic systems, Shivakumar brings unparalleled expertise in system design, troubleshooting, and optimization.</p>
            </div>

            <div className="team-member" ref={(el) => (sectionsRef.current[13] = el)}>
              <div className="team-member-image">
                <img
                  src={profile}
                  alt="Binod Kumar - Hydraulic Service Specialist"
                />
              </div>
              <h3>Binod Kumar</h3>
              <p className="role">Hydraulic Service Specialist</p>
              <p className="bio">With 20 years of hands-on experience, Binod specializes in hydraulic system maintenance, repair, and service optimization.</p>
            </div>

            <div className="team-member" ref={(el) => (sectionsRef.current[14] = el)}>
              <div className="team-member-image">
                <img
                  src={profile}
                  alt="Sreenivasan K - Crushers & Screens Expert"
                />
              </div>
              <h3>Sreenivasan K</h3>
              <p className="role">Crushers & Screens Expert</p>
              <p className="bio">With 15 years of specialized experience, Sreenivasan is our go-to expert for all crusher and screen hydraulic systems and components.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      {/* <section className="values-section">
        <div className="values-container">
          <h2 className="section-title">Our Values</h2>
          <p className="section-subtitle">
            The principles that guide everything we do
          </p>
          <div className="values-grid">
            {values.map((value, index) => (
              <div 
                key={index} 
                ref={(el) => (sectionsRef.current[7 + index] = el)}
                className="value-card"
              >
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Customer Commitment */}
      <section className="commitment-section">
        <div className="commitment-container">
          <div className="commitment-content" ref={(el) => (sectionsRef.current[11] = el)}>
            <h2>Our Commitment to You</h2>
            <p>
              Building long-term relationships through transparency, trust, and dedicated support. We understand that your equipment is critical to your business operations, and we're committed to keeping it running at peak performance.
            </p>
            <p>
              Every service call, every repair, and every part we supply is backed by our commitment to your success. We're not just service providers – we're your partners in productivity.
            </p>
            <button className="btn-primary" onClick={() => navigate('/contact')}>
              Schedule a Service
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;